import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../../lib/api';
import { facturesOuvertesClient, soldesClients } from '../../lib/argentApi';
import { messageErreur } from '../../lib/erreurs';
import { formatDate, formatMontant } from '../../lib/formats';
import { nomLisible, referenceLisible } from '../../lib/libelles';
import { useSyncOuEnLigne } from '../../context/SyncContext';
import { usePagination } from '../../hooks/usePagination';
import FinancialStatement, { financialKey } from '../FinancialStatement';
import { BarrePagination } from '../ui/pagination-bar';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { LoadingState } from '../ui/loading-state';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { toast } from '../ui/sonner';
import DetailEcriture from './DetailEcriture';
import { EncaisserClient } from './NouvelleOperation';
import TableauSoldes, { ilYA } from './TableauSoldes';

/**
 * Onglet « Clients » de l'écran Argent (revue de l'écran Argent, lot 5).
 *
 * Qui nous doit, depuis quand: une ligne par client, toutes périodes, avec
 * Facturé, Avoirs (seulement s'il y en a), Encaissé et Reste dû qui
 * s'additionnent, et l'ancienneté de la plus vieille facture ouverte. Un clic
 * ouvre le panneau du client (factures ouvertes, relevé, « Encaisser »).
 * « Encaisser » ouvre la même fenêtre que le menu « Nouvelle opération » et
 * que la page Factures.
 */

const pluriel = (n, mot) => `${n} ${mot}${n > 1 ? 's' : ''}`;

function PanneauClient({ clientId, ligne, peutSaisir, onFermer, onEncaisser, rafraichir, onModifie }) {
  const [factures, setFactures] = useState(null);
  const [erreur, setErreur] = useState('');
  const [ecriture, setEcriture] = useState(null);
  const charger = useCallback(async () => {
    if (!clientId) return;
    try {
      setFactures(await facturesOuvertesClient(clientId));
      setErreur('');
    } catch (e) {
      setErreur(messageErreur(e, 'Les factures ouvertes sont indisponibles.'));
    }
  }, [clientId]);
  useEffect(() => {
    setFactures(null);
    charger();
  }, [charger, rafraichir]);
  const devise = factures?.currency || 'XOF';
  const nom = nomLisible(ligne?.name || factures?.client?.name, 'Client');
  return (
    <Sheet open={Boolean(clientId)} onOpenChange={(ouvert) => !ouvert && onFermer()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-4xl" data-testid="panneau-client">
        <SheetHeader>
          <SheetTitle>{nom}</SheetTitle>
          <SheetDescription>
            {ligne ? `Reste dû ${formatMontant(ligne.remaining, devise)} · facturé ${formatMontant(ligne.billed, devise)} · encaissé ${formatMontant(ligne.collected, devise)}` : 'Situation du client'}
          </SheetDescription>
        </SheetHeader>
        {peutSaisir && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={() => onEncaisser({ client: clientId })}>Encaisser</Button>
          </div>
        )}
        <section className="mt-5 space-y-2" aria-label="Factures ouvertes">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Factures ouvertes</h3>
          {erreur && <p role="alert" className="text-sm text-destructive">{erreur}</p>}
          {!factures && !erreur && <LoadingState shape="table" rows={3} columns={5} label="Chargement des factures" />}
          {factures && factures.items.length === 0 && <p className="text-sm text-muted-foreground">Aucune facture ouverte.</p>}
          {factures && factures.items.length > 0 && (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5 font-semibold">N°</th>
                    <th className="px-2 py-1.5 font-semibold">Date</th>
                    <th className="hidden px-2 py-1.5 text-right font-semibold sm:table-cell">Montant</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Reste</th>
                    <th className="hidden px-2 py-1.5 font-semibold sm:table-cell">Depuis</th>
                    {peutSaisir && <th className="px-2 py-1.5"><span className="sr-only">Action</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {factures.items.map((f) => (
                    <tr key={f.id} className="border-b last:border-0">
                      <td className="whitespace-nowrap px-2 py-1.5">
                        <Link className="text-primary hover:underline" to={`/invoices?invoice_id=${encodeURIComponent(f.id)}`}>{referenceLisible(f.invoice_number, 'Facture')}</Link>
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 tabular-nums">{formatDate(f.invoice_date)}</td>
                      <td className="hidden whitespace-nowrap px-2 py-1.5 text-right tabular-nums sm:table-cell">{formatMontant(f.total, devise)}</td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums">{formatMontant(f.remaining, devise)}</td>
                      <td className="hidden whitespace-nowrap px-2 py-1.5 text-muted-foreground sm:table-cell">{ilYA(f.age_days)}</td>
                      {peutSaisir && (
                        <td className="whitespace-nowrap px-2 py-1 text-right">
                          <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => onEncaisser({ client: clientId, facture: f.id })}>Encaisser</Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t bg-muted/50 font-semibold">
                  <tr>
                    <td className="px-2 py-1.5" colSpan={2}>Total ({pluriel(factures.items.length, 'facture')})</td>
                    <td className="hidden whitespace-nowrap px-2 py-1.5 text-right tabular-nums sm:table-cell">{formatMontant(factures.items.reduce((t, f) => t + Number(f.total || 0), 0), devise)}</td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums">{formatMontant(factures.items.reduce((t, f) => t + Number(f.remaining || 0), 0), devise)}</td>
                    <td className="hidden sm:table-cell" />
                    {peutSaisir && <td />}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </section>
        <div className="mt-5">
          <FinancialStatement partyType="CUSTOMER" partyId={clientId} refresh={rafraichir} onSelect={(operation) => setEcriture(operation.id)} readOnly={false} />
        </div>
        <DetailEcriture operationId={ecriture} onFermer={() => setEcriture(null)} onModifie={onModifie} peutSaisir={peutSaisir} />
      </SheetContent>
    </Sheet>
  );
}

/** Soldes historiques à rapprocher: reprise des pièces identifiées, client par client. */
function SoldesHistoriques({ ouvert, onFermer, onOuvrirClient, onModifie }) {
  const enLigne = useSyncOuEnLigne().isOnline !== false;
  const [lignes, setLignes] = useState(null);
  const [envoi, setEnvoi] = useState('');
  const charger = useCallback(async () => {
    try {
      const reponse = await api.get('/finance/history-review');
      const corps = reponse?.data;
      setLignes((Array.isArray(corps) ? corps : corps?.data || []).filter((l) => l.requires_review));
    } catch (e) {
      setLignes([]);
      toast.error(messageErreur(e, 'Les soldes historiques sont indisponibles.'));
    }
  }, []);
  useEffect(() => {
    if (ouvert) charger();
  }, [ouvert, charger]);
  const pagination = usePagination(lignes || [], { cle: 'historiques' });
  const reprendre = async (clientId) => {
    setEnvoi(clientId);
    try {
      await api.post(`/finance/clients/${clientId}/adopt-history`, { idempotency_key: financialKey() });
      toast.success('Pièces identifiées reprises.');
      await charger();
      onModifie?.();
    } catch (e) {
      toast.error(messageErreur(e, 'La reprise a échoué.'));
    } finally {
      setEnvoi('');
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onFermer()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Soldes historiques à rapprocher</DialogTitle></DialogHeader>
        {!lignes && <LoadingState shape="table" rows={3} columns={4} label="Chargement" />}
        {lignes && lignes.length === 0 && <p className="text-sm text-muted-foreground">Aucun solde à rapprocher.</p>}
        {lignes && lignes.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="py-1.5 font-semibold">Client</th>
                <th className="py-1.5 text-right font-semibold">Solde historique</th>
                <th className="py-1.5 text-right font-semibold">Selon les pièces</th>
                <th className="py-1.5"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {pagination.lignes.map((l) => (
                <tr key={l.client_id} className="border-b last:border-0">
                  <td className="py-1.5"><button type="button" className="text-left text-primary hover:underline" onClick={() => onOuvrirClient(l.client_id)}>{nomLisible(l.name, 'Client')}</button></td>
                  <td className="py-1.5 text-right tabular-nums">{formatMontant(l.stored_balance, l.currency)}</td>
                  <td className="py-1.5 text-right tabular-nums">{formatMontant(l.document_balance, l.currency)}</td>
                  <td className="py-1.5 text-right">
                    <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={!enLigne || envoi === l.client_id} onClick={() => reprendre(l.client_id)}>Reprendre les pièces</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {lignes && <BarrePagination pagination={pagination} nom="clients" />}
      </DialogContent>
    </Dialog>
  );
}

export default function ClientsArgent({ clientChoisi, onChoisirClient, peutSaisir = true, soldesARapprocher = 0, onModifie }) {
  const [encaisser, setEncaisser] = useState(null);
  const [rafraichir, setRafraichir] = useState(0);
  const [historique, setHistorique] = useState(false);
  const [lignesConnues, setLignesConnues] = useState({});
  const apresGeste = () => {
    setEncaisser(null);
    setRafraichir((n) => n + 1);
    onModifie?.();
  };
  const charger = useCallback(async (params) => {
    const donnees = await soldesClients(params);
    setLignesConnues((precedent) => ({ ...precedent, ...Object.fromEntries((donnees?.items || []).map((l) => [l.client_id, l])) }));
    return donnees;
  }, []);
  const colonnes = [
    { cle: 'billed', titre: 'Facturé', montant: true, tri: 'facture' },
    { cle: 'credits', titre: 'Avoirs', montant: true, masquee: (totaux) => !Number(totaux.credits) },
    { cle: 'collected', titre: 'Encaissé', montant: true },
    { cle: 'remaining', titre: 'Reste dû', montant: true, tri: 'reste', classe: 'font-medium' },
    {
      cle: 'oldest_open_age_days',
      titre: 'Plus ancienne',
      tri: 'anciennete',
      rendu: (ligne) => (ligne.oldest_open_date ? <span title={`Facture du ${formatDate(ligne.oldest_open_date)}`}>{ilYA(ligne.oldest_open_age_days)}</span> : ''),
      total: (totaux) => (Number(totaux.open_invoices) ? pluriel(Number(totaux.open_invoices), 'facture') : ''),
    },
  ];
  return (
    <>
      <TableauSoldes
        cle="clients"
        testid="tableau-clients"
        charger={charger}
        colonnes={colonnes}
        identifiant={(l) => l.client_id}
        nom={{ titre: 'Client', tri: 'nom', rendu: (l) => nomLisible(l.name, 'Client') }}
        detailMobile={(l) => [l.oldest_open_date ? ilYA(l.oldest_open_age_days) : null, l.open_invoices ? pluriel(l.open_invoices, 'facture') : null].filter(Boolean).join(' · ')}
        montantMobile={{ cle: 'remaining', titre: 'Reste dû' }}
        filtre={{ libelle: 'Avec reste dû', param: 'avec_reste' }}
        triParDefaut="reste"
        rechercheLibelle="Rechercher un client…"
        totalLibelle={(n) => `Total (${pluriel(n, 'client')})`}
        domaines={['finance', 'paiements', 'factures', 'clients']}
        rafraichir={rafraichir}
        vide="Aucun client ne doit d’argent."
        action={peutSaisir ? (l) => (Number(l.remaining) > 0 ? (
          <Button type="button" size="sm" variant="outline" className="h-7 px-2.5 text-xs" onClick={() => setEncaisser({ client: l.client_id })}>Encaisser</Button>
        ) : null) : undefined}
        onOuvrir={(l) => onChoisirClient(l.client_id)}
        barre={soldesARapprocher > 0 ? (
          <button type="button" className="inline-flex h-8 items-center rounded-full border border-flame/30 bg-flame/10 px-3 text-xs font-medium text-flame-ink" onClick={() => setHistorique(true)}>
            {`Soldes historiques à rapprocher (${soldesARapprocher})`}
          </button>
        ) : null}
      />
      <PanneauClient
        clientId={clientChoisi || null}
        ligne={lignesConnues[clientChoisi]}
        peutSaisir={peutSaisir}
        onFermer={() => onChoisirClient('')}
        onEncaisser={setEncaisser}
        rafraichir={rafraichir}
        onModifie={apresGeste}
      />
      <EncaisserClient
        ouvert={Boolean(encaisser)}
        client={encaisser?.client}
        facture={encaisser?.facture}
        onFermer={() => setEncaisser(null)}
        onTermine={apresGeste}
      />
      <SoldesHistoriques ouvert={historique} onFermer={() => setHistorique(false)} onOuvrirClient={(id) => { setHistorique(false); onChoisirClient(id); }} onModifie={apresGeste} />
    </>
  );
}
